import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Map as MapIcon, Table, Plus, Minus, Globe, Crosshair, Clock, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import GEO from '../data/europeZones.json';

// Europe Map: day-ahead price per bidding zone (fill) and Nord Pool day-ahead
// scheduled exchanges per border (arrows), one CET delivery day, scrubbed by
// quarter or hour. Geometry: src/data/europeZones.json, pre-projected by
// scripts/build_europe_zones.py, so no geo library is needed here.

const WAW_TZ = 'Europe/Warsaw';
const [, , VB_W, VB_H] = GEO.viewBox;
const ZONES = GEO.zones;
const ZONE_BY_ID = Object.fromEntries(ZONES.map((z) => [z.id, z]));
const BORDER_BY_KEY = Object.fromEntries(GEO.borders.map((b) => [`${b.a}|${b.b}`, b]));
const FULL_FRAME = [0, 0, VB_W, VB_H];

// Price is magnitude -> one sequential hue (blue 100..700). Dark mode flips the
// anchor so low prices recede into the dark surface.
const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];
const WHITE_INK = new Set(['#256abf', '#184f95', '#0d366b']);
const THEMES = {
  light: { sea: '#fcfcfb', outside: '#f0efec', noData: '#e1e0d9', gap: '#fcfcfb', ink: '#0b0b0b', ink2: '#52514e', muted: '#898781', halo: '#fcfcfb', ramp: RAMP },
  dark: { sea: '#1a1a19', outside: '#2c2c2a', noData: '#383835', gap: '#1a1a19', ink: '#f0efec', ink2: '#c3c2b7', muted: '#898781', halo: '#1a1a19', ramp: [...RAMP].reverse() },
};

const SOURCE_NAMES = { entsoe: 'ENTSO-E', nordpool: 'Nord Pool', nordpool_pl: 'Nord Pool (PL borders)', nordpool_de: 'Nord Pool (DE borders)', nordpool_fr: 'Nord Pool (FR borders)' };

const todayWaw = () => new Intl.DateTimeFormat('en-CA', { timeZone: WAW_TZ }).format(new Date());
const shiftDate = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};
const zoneLabel = (id) => ZONE_BY_ID[id]?.label || id;
const fmtPrice = (v) => (v == null ? '–' : `${v < 0 ? '−' : ''}€${Math.abs(Math.round(v))}`);
const fmt2 = (v) => (v == null ? '–' : v.toFixed(2));
// Round the magnitude, so -5161.5 and 5161.5 both read 5,162 and -0.1 reads 0, not -0.
const fmtMw = (v) => {
  if (v == null) return '–';
  const r = Math.round(Math.abs(v));
  return (r === 0 ? 0 : Math.sign(v) * r).toLocaleString('en-US');
};
// Net position wording; PL often clears at exactly 0.
const netWord = (v) => (Math.abs(v) < 0.5 ? 'Balanced' : v > 0 ? 'Importing' : 'Exporting');

// Mean of each time group (one quarter, or the four quarters of an hour).
function aggregate(data, resolution) {
  const n = data.slots.length;
  const size = resolution === 60 ? 4 : 1;
  const groups = [];
  for (let i = 0; i < n; i += size) groups.push(Array.from({ length: Math.min(size, n - i) }, (_, k) => i + k));
  const mean = (arr) => groups.map((g) => {
    const xs = g.map((i) => arr[i]).filter((x) => x != null);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
  });
  const mapValues = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, e]) => [k, { ...e, values: mean(e.values) }]));
  return {
    starts: groups.map((g) => data.labels[g[0]]),
    ends: groups.map((g, k) => (k + 1 < groups.length ? data.labels[groups[k + 1][0]] : '24:00')),
    slotsUtc: groups.map((g) => data.slots[g[0]]),
    prices: mapValues(data.prices),
    flows: (data.flows || []).map((f) => ({ ...f, values: mean(f.values) })),
    net: mapValues(data.net_positions),
  };
}

// Quantile bins over the whole day (all zones, all times), so a zone's colour
// only changes when its price does, not when you scrub the slider.
function priceBins(prices) {
  const v = Object.values(prices).flatMap((e) => e.values).filter((x) => x != null).sort((a, b) => a - b);
  if (!v.length) return null;
  const range = v[v.length - 1] - v[0];
  const unit = range > 200 ? 10 : range > 40 ? 5 : 1;
  const q = (p) => v[Math.min(v.length - 1, Math.floor(p * (v.length - 1)))];
  const edges = [];
  for (let k = 1; k < RAMP.length; k += 1) {
    const e = Math.round(q(k / RAMP.length) / unit) * unit;
    if (e > v[0] && (!edges.length || e > edges[edges.length - 1])) edges.push(e);
  }
  return { edges, min: v[0], max: v[v.length - 1] };
}
const binIndex = (bins, x) => {
  let i = 0;
  while (i < bins.edges.length && x >= bins.edges[i]) i += 1;
  const nb = bins.edges.length + 1;
  return nb === 1 ? 3 : Math.round((i * (RAMP.length - 1)) / (nb - 1));
};
const binLabels = (bins) => {
  const e = bins.edges;
  if (!e.length) return [`${Math.round(bins.min)}–${Math.round(bins.max)}`];
  return [`< ${e[0]}`, ...e.slice(1).map((x, i) => `${e[i]} – ${x}`), `≥ ${e[e.length - 1]}`];
};

// View = {x, y, w}; height follows the container's aspect.
const fitFrame = ([fx, fy, fw, fh], aspect) => {
  if (fw / fh > aspect) return { x: fx, y: fy + fh / 2 - fw / aspect / 2, w: fw };
  return { x: fx + fw / 2 - (fh * aspect) / 2, y: fy, w: fh * aspect };
};

const ZoneMap = ({ series, idx, bins, theme, onHover, hover }) => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [size, setSize] = useState(null); // measured container, px
  const [view, setView] = useState(null);
  const aspect = size ? size.w / size.h : 1.4;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Frame the default view once the container has a real size.
  useEffect(() => {
    if (!view && size) setView(fitFrame(GEO.coreFrame, aspect));
  }, [aspect, view, size]);

  const v = view || fitFrame(GEO.coreFrame, aspect);
  const vh = v.w / aspect;
  const ppu = (size ? size.w : 1000) / v.w; // screen px per map unit
  const u = (px) => px / ppu; // constant screen size at any zoom
  // Zoomed far out (whole Europe) the MW labels pile up; arrows stay, values on hover.
  const showFlowLabels = ppu >= 1.0;

  const zoomBy = useCallback((factor, mx = 0.5, my = 0.5) => {
    setView((prev) => {
      const cur = prev || fitFrame(GEO.coreFrame, aspect);
      const curH = cur.w / aspect;
      const w = Math.min(VB_W * 1.4, Math.max(90, cur.w * factor));
      const ux = cur.x + mx * cur.w;
      const uy = cur.y + my * curH;
      return { x: ux - mx * w, y: uy - my * (w / aspect), w };
    });
  }, [aspect]);

  // Wheel zoom needs a non-passive listener to keep the page from scrolling.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomBy(e.deltaY > 0 ? 1.15 : 1 / 1.15, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, view: v, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 3) return;
    d.moved = true;
    onHover(null);
    setView({ x: d.view.x - dx / ppu, y: d.view.y - dy / ppu, w: d.view.w });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const hoverAt = (e, item) => {
    if (dragRef.current?.moved) return;
    const r = wrapRef.current.getBoundingClientRect();
    onHover({ ...item, x: e.clientX - r.left, y: e.clientY - r.top, w: r.width });
  };

  const priceAt = (id) => series?.prices[id]?.values[idx] ?? null;
  const fillOf = (id) => {
    const p = priceAt(id);
    return p == null || !bins ? theme.noData : theme.ramp[binIndex(bins, p)];
  };

  const arrows = (series?.flows || []).map((f) => {
    const val = f.values[idx];
    if (val == null || !ZONE_BY_ID[f.a] || !ZONE_BY_ID[f.b]) return null;
    const [from, to] = val >= 0 ? [f.a, f.b] : [f.b, f.a];
    const pa = ZONE_BY_ID[from].anchor;
    const pb = ZONE_BY_ID[to].anchor;
    let dx = pb[0] - pa[0];
    let dy = pb[1] - pa[1];
    const len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;
    const geo = BORDER_BY_KEY[`${f.a}|${f.b}`] || { x: (pa[0] + pb[0]) / 2, y: (pa[1] + pb[1]) / 2 };
    const half = u(15);
    const head = u(6.5);
    const zero = Math.abs(val) < 0.5;
    const x1 = geo.x - dx * half; const y1 = geo.y - dy * half;
    const x2 = geo.x + dx * half; const y2 = geo.y + dy * half;
    let nx = -dy; let ny = dx;
    if (ny > 0 || (Math.abs(ny) < 0.2 && nx < 0)) { nx = -nx; ny = -ny; }
    const lx = geo.x + nx * u(11);
    const ly = geo.y + ny * u(11);
    return { key: `${f.a}|${f.b}`, f, val, from, to, x1, y1, x2, y2, dx, dy, head, zero, lx, ly };
  }).filter(Boolean);

  const btn = 'inline-flex h-8 w-8 items-center justify-center bg-white text-gray-700 hover:bg-gray-50 border-gray-300';

  return (
    <div ref={wrapRef} className="relative h-full w-full overflow-hidden rounded-lg border border-gray-200" style={{ background: theme.sea }}>
      <svg
        ref={svgRef}
        viewBox={`${v.x} ${v.y} ${v.w} ${vh}`}
        width="100%"
        height="100%"
        className="block select-none touch-none"
        style={{ cursor: dragRef.current?.moved ? 'grabbing' : 'grab' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => onHover(null)}
        role="img"
        aria-label="Map of European bidding zones with day-ahead prices and scheduled exchanges"
      >
        <path d={GEO.background} fill={theme.outside} />
        {ZONES.map((z) => (
          <path
            key={z.id}
            d={z.path}
            fill={fillOf(z.id)}
            stroke={theme.gap}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            onMouseMove={(e) => hoverAt(e, { kind: 'zone', id: z.id })}
          />
        ))}
        {hover?.kind === 'zone' && ZONE_BY_ID[hover.id] && (
          <path d={ZONE_BY_ID[hover.id].path} fill="none" stroke={theme.ink} strokeWidth={1.5} vectorEffect="non-scaling-stroke" pointerEvents="none" />
        )}

        {ZONES.map((z) => {
          const p = priceAt(z.id);
          const [bw, bh] = z.labelBox;
          const wide = bw * ppu;
          const tall = bh * ppu;
          if (p == null) {
            if (wide < 20 || tall < 12) return null;
            return (
              <text key={z.id} x={z.anchor[0]} y={z.anchor[1]} fontSize={u(10)} textAnchor="middle" dominantBaseline="middle" fill={theme.muted} pointerEvents="none">
                {z.label}
              </text>
            );
          }
          if (wide < 30 || tall < 22) return null;
          const fill = fillOf(z.id);
          const ink = WHITE_INK.has(fill) ? '#ffffff' : '#0b0b0b';
          return (
            <g key={z.id} pointerEvents="none">
              <text x={z.anchor[0]} y={z.anchor[1] - u(6)} fontSize={u(9.5)} textAnchor="middle" fill={ink} opacity={0.8}>{z.label}</text>
              <text x={z.anchor[0]} y={z.anchor[1] + u(7)} fontSize={u(12.5)} fontWeight={600} textAnchor="middle" fill={ink}>{fmtPrice(p)}</text>
            </g>
          );
        })}

        {arrows.map((a) => {
          const hx = a.x2 - a.dx * a.head * 0.9;
          const hy = a.y2 - a.dy * a.head * 0.9;
          const wing = a.head * 0.6;
          const headPts = `${a.x2},${a.y2} ${hx - a.dy * wing},${hy + a.dx * wing} ${hx + a.dy * wing},${hy - a.dx * wing}`;
          const isHover = hover?.kind === 'flow' && hover.id === a.key;
          return (
            <g key={a.key} onMouseMove={(e) => hoverAt(e, { kind: 'flow', id: a.key })}>
              <line x1={a.x1} y1={a.y1} x2={a.zero ? a.x2 : hx} y2={a.zero ? a.y2 : hy} stroke={theme.halo} strokeWidth={4.5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              {!a.zero && <polygon points={headPts} fill={theme.ink} stroke={theme.halo} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />}
              <line x1={a.x1} y1={a.y1} x2={a.zero ? a.x2 : hx} y2={a.zero ? a.y2 : hy} stroke={theme.ink} strokeWidth={isHover ? 2.6 : 1.8} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              {showFlowLabels && (
              <text
                x={a.lx}
                y={a.ly}
                fontSize={u(10)}
                fontWeight={600}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={theme.ink}
                stroke={theme.halo}
                strokeWidth={u(3)}
                paintOrder="stroke"
                strokeLinejoin="round"
              >
                {fmtMw(Math.abs(a.val))}
              </text>
              )}
              {/* hit area bigger than the mark */}
              <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" />
              <circle cx={a.lx} cy={a.ly} r={u(12)} fill="transparent" />
            </g>
          );
        })}
      </svg>

      <div className="absolute left-3 top-3 flex flex-col overflow-hidden rounded-md border border-gray-300 shadow-sm">
        <button type="button" className={`${btn} border-b`} onClick={() => zoomBy(1 / 1.5)} title="Zoom in" aria-label="Zoom in"><Plus className="h-4 w-4" /></button>
        <button type="button" className={`${btn} border-b`} onClick={() => zoomBy(1.5)} title="Zoom out" aria-label="Zoom out"><Minus className="h-4 w-4" /></button>
        <button type="button" className={`${btn} border-b`} onClick={() => setView(fitFrame(GEO.coreFrame, aspect))} title="Central Europe" aria-label="Central Europe"><Crosshair className="h-4 w-4" /></button>
        <button type="button" className={btn} onClick={() => setView(fitFrame(FULL_FRAME, aspect))} title="Whole Europe" aria-label="Whole Europe"><Globe className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const Tooltip = ({ hover, series, idx, timeLabel }) => {
  if (!hover || !series) return null;
  let body = null;
  if (hover.kind === 'zone') {
    const z = ZONE_BY_ID[hover.id];
    const e = series.prices[hover.id];
    const vals = (e?.values || []).filter((x) => x != null);
    const net = series.net[hover.id]?.values[idx];
    body = (
      <>
        <div className="font-semibold text-gray-900">{z?.name || hover.id}</div>
        {e ? (
          <>
            <div className="mt-1 text-gray-900"><span className="text-lg font-semibold">{fmt2(e.values[idx])}</span> <span className="text-gray-500">€/MWh at {timeLabel}</span></div>
            <div className="text-gray-600">Day avg {fmt2(vals.reduce((a, b) => a + b, 0) / vals.length)} · min {fmt2(Math.min(...vals))} · max {fmt2(Math.max(...vals))}</div>
            <div className="mt-1 text-xs text-gray-500">Source: {e.sources.map((s) => SOURCE_NAMES[s] || s).join(' + ')}</div>
          </>
        ) : (
          <div className="mt-1 text-gray-500">No price data for this day</div>
        )}
        {net != null && <div className="mt-1 text-gray-700">Net: {netWord(net)}{Math.abs(net) < 0.5 ? '' : ` ${fmtMw(Math.abs(net))} MW`} (scheduled)</div>}
      </>
    );
  } else {
    const f = series.flows.find((x) => `${x.a}|${x.b}` === hover.id);
    if (!f) return null;
    const val = f.values[idx];
    const [from, to] = val >= 0 ? [f.a, f.b] : [f.b, f.a];
    body = (
      <>
        <div className="font-semibold text-gray-900">{zoneLabel(from)} → {zoneLabel(to)}</div>
        <div className="mt-1 text-gray-900"><span className="text-lg font-semibold">{fmtMw(Math.abs(val))}</span> <span className="text-gray-500">MW at {timeLabel}</span></div>
        <div className="mt-1 text-xs text-gray-500">Day-ahead scheduled exchange · {f.sources.map((s) => SOURCE_NAMES[s] || s).join(' + ')}</div>
      </>
    );
  }
  const left = hover.x + 16 + 260 > hover.w ? hover.x - 16 - 260 : hover.x + 16;
  return (
    <div className="pointer-events-none absolute z-10 w-[260px] rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-xl" style={{ left, top: Math.max(8, hover.y - 20) }}>
      {body}
    </div>
  );
};

const NetPositionCard = ({ id, value }) => {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div>
        <div className="text-sm font-semibold uppercase tracking-wide text-gray-800">{ZONE_BY_ID[id]?.name || id}</div>
        <div className="text-sm text-gray-600">{value == null ? 'No data' : netWord(value)}</div>
      </div>
      <div className="text-right">
        <span className="text-2xl font-bold tabular-nums text-gray-900">{value == null ? '–' : fmtMw(Math.abs(value))}</span>
        <span className="ml-1 text-sm font-semibold text-gray-600">MW</span>
      </div>
    </div>
  );
};

const Legend = ({ bins, theme }) => (
  <div className="space-y-2">
    <div className="text-sm font-semibold text-gray-800">Day-ahead price, €/MWh</div>
    {bins ? (
      <div className="space-y-1">
        {binLabels(bins).map((label, i) => (
          <div key={label} className="flex items-center gap-2 text-xs text-gray-700">
            <span className="inline-block h-3 w-6 rounded-sm" style={{ background: theme.ramp[binIndex(bins, i === 0 ? bins.min : bins.edges[i - 1])] }} />
            <span className="tabular-nums">{label}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-xs text-gray-500">No prices for this day</div>
    )}
    <div className="flex items-center gap-2 pt-1 text-xs text-gray-700">
      <span className="inline-block h-3 w-6 rounded-sm" style={{ background: theme.noData }} />
      No price data
    </div>
    <div className="flex items-center gap-2 text-xs text-gray-700">
      <svg width="24" height="12" aria-hidden="true"><line x1="2" y1="6" x2="16" y2="6" stroke={theme.ink} strokeWidth="1.8" /><polygon points="22,6 15,2.5 15,9.5" fill={theme.ink} /></svg>
      Scheduled exchange, MW
    </div>
  </div>
);

const DataTable = ({ columns, rows, idx, onSelect, footer }) => {
  const boxRef = useRef(null);
  const selectedRef = useRef(null);
  // Keep the selected period's row in view inside the table only (not the page),
  // clear of the sticky header and footer.
  useEffect(() => {
    const box = boxRef.current;
    const row = selectedRef.current;
    if (!box || !row) return;
    const head = box.querySelector('thead')?.offsetHeight || 0;
    const foot = box.querySelector('tfoot')?.offsetHeight || 0;
    if (row.offsetTop - head < box.scrollTop) box.scrollTop = row.offsetTop - head;
    else if (row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight - foot) {
      box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight + foot;
    }
  }, [idx]);
  const cell = (c) => `whitespace-nowrap px-3 py-1.5 tabular-nums ${c.key === 'time' ? 'text-left text-gray-700' : 'text-right text-gray-900'}`;
  return (
    <div ref={boxRef} className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-10 bg-gray-100">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`whitespace-nowrap px-3 py-2 font-semibold text-gray-700 ${c.key === 'time' ? 'text-left' : 'text-right'}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.time}
              ref={i === idx ? selectedRef : null}
              onClick={() => onSelect(i)}
              className={`cursor-pointer border-t border-gray-100 ${i === idx ? 'bg-blue-50 font-semibold' : 'hover:bg-gray-50'}`}
            >
              {columns.map((c) => <td key={c.key} className={cell(c)}>{r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot className="sticky bottom-0 bg-gray-100">
            {footer.map((r) => (
              <tr key={r.time} className="border-t border-gray-200 font-semibold">
                {columns.map((c) => <td key={c.key} className={cell(c)}>{r[c.key]}</td>)}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
};

const pricesTable = (series) => {
  const ids = Object.keys(series.prices).sort((a, b) => (a === 'PL' ? -1 : b === 'PL' ? 1 : zoneLabel(a).localeCompare(zoneLabel(b))));
  const columns = [{ key: 'time', label: 'Time' }, ...ids.map((id) => ({ key: id, label: zoneLabel(id) }))];
  const rows = series.starts.map((t, i) => ({ time: `${t}–${series.ends[i]}`, ...Object.fromEntries(ids.map((id) => [id, fmt2(series.prices[id].values[i])])) }));
  const stat = (name, fn) => ({ time: name, ...Object.fromEntries(ids.map((id) => {
    const xs = series.prices[id].values.filter((x) => x != null);
    return [id, xs.length ? fmt2(fn(xs)) : '–'];
  })) });
  const footer = [stat('Avg', (xs) => xs.reduce((a, b) => a + b, 0) / xs.length), stat('Min', (xs) => Math.min(...xs)), stat('Max', (xs) => Math.max(...xs))];
  return { columns, rows, footer };
};

const flowsTable = (series) => {
  const netIds = Object.keys(series.net);
  const columns = [
    { key: 'time', label: 'Time' },
    ...netIds.map((id) => ({ key: `net_${id}`, label: `${zoneLabel(id)} net` })),
    ...series.flows.map((f) => ({ key: `${f.a}|${f.b}`, label: `${zoneLabel(f.a)} → ${zoneLabel(f.b)}` })),
  ];
  const rows = series.starts.map((t, i) => ({
    time: `${t}–${series.ends[i]}`,
    ...Object.fromEntries(netIds.map((id) => [`net_${id}`, fmtMw(series.net[id].values[i])])),
    ...Object.fromEntries(series.flows.map((f) => [`${f.a}|${f.b}`, fmtMw(f.values[i])])),
  }));
  return { columns, rows };
};

const EuropeMap = ({ darkMode = false }) => {
  const { fetchGet } = useApi();
  const [date, setDate] = useState(todayWaw);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolution, setResolution] = useState(15);
  const [idx, setIdx] = useState(null);
  const [tab, setTab] = useState('map');
  const [hover, setHover] = useState(null);
  const theme = darkMode ? THEMES.dark : THEMES.light;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchGet(`/europe-map?date=${date}`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.status !== 'success') throw new Error(json.message || 'Failed to load the map data');
        setData(json);
      })
      .catch((err) => { if (!cancelled) { setError(err.message); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, fetchGet]);

  const series = useMemo(() => (data ? aggregate(data, resolution) : null), [data, resolution]);
  const bins = useMemo(() => (series ? priceBins(series.prices) : null), [series]);
  const count = series ? series.starts.length : 0;

  const nowIndex = useCallback(() => {
    if (!series || date !== todayWaw()) return null;
    const now = Date.now();
    let k = 0;
    series.slotsUtc.forEach((s, i) => { if (Date.parse(s) <= now) k = i; });
    return k;
  }, [series, date]);

  // First load of a day: today -> the current quarter; otherwise keep the clock position.
  useEffect(() => {
    if (!series) return;
    setIdx((prev) => {
      if (prev == null) return nowIndex() ?? Math.min(count - 1, Math.round((8 * 60) / resolution));
      return Math.min(prev, count - 1);
    });
  }, [series, count, nowIndex, resolution]);

  const changeResolution = (res) => {
    if (res === resolution) return;
    setIdx((prev) => (prev == null ? prev : res === 60 ? Math.floor(prev / 4) : prev * 4));
    setResolution(res);
  };

  const i = idx == null ? 0 : Math.min(idx, Math.max(0, count - 1));
  const timeLabel = series && count ? `${series.starts[i]}–${series.ends[i]}` : '';
  const pricedZones = series ? Object.keys(series.prices).length : 0;
  const isToday = date === todayWaw();

  const tabBtn = (key, label, Icon) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`inline-flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium ${tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
  const ctrlBtn = 'inline-flex h-10 items-center justify-center rounded-lg border border-gray-300 bg-white px-2.5 text-gray-700 hover:bg-gray-50 disabled:opacity-40';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Europe Map</h1>
        <p className="text-gray-600">Day-ahead prices per bidding zone and day-ahead scheduled exchanges between zones.</p>
      </div>

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <div className="mb-1 text-sm font-medium text-gray-700">Delivery date</div>
          <div className="flex items-center gap-1">
            <button type="button" className={ctrlBtn} onClick={() => setDate(shiftDate(date, -1))} aria-label="Previous day"><ChevronLeft className="h-4 w-4" /></button>
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="h-10 rounded-lg border border-gray-300 px-3 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
            <button type="button" className={ctrlBtn} onClick={() => setDate(shiftDate(date, 1))} aria-label="Next day"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="min-w-[320px] flex-1">
          <div className="mb-1 flex items-center justify-between text-sm font-medium text-gray-700">
            <span>Time (CET)</span>
            <span className="tabular-nums text-gray-900">{timeLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className={ctrlBtn} disabled={!count || i === 0} onClick={() => setIdx(Math.max(0, i - 1))} aria-label="Previous period"><ChevronLeft className="h-4 w-4" /></button>
            <input
              type="range"
              min={0}
              max={Math.max(0, count - 1)}
              value={i}
              disabled={!count}
              onChange={(e) => setIdx(Number(e.target.value))}
              className="h-2 flex-1 cursor-pointer accent-blue-600"
              aria-label="Delivery period"
            />
            <button type="button" className={ctrlBtn} disabled={!count || i >= count - 1} onClick={() => setIdx(Math.min(count - 1, i + 1))} aria-label="Next period"><ChevronRight className="h-4 w-4" /></button>
            {isToday && (
              <button type="button" className={`${ctrlBtn} gap-1 text-sm`} onClick={() => { const k = nowIndex(); if (k != null) setIdx(k); }} title="Jump to the current period">
                <Clock className="h-4 w-4" /> Now
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-gray-700">Resolution</div>
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-300">
            {[15, 60].map((res) => (
              <button
                key={res}
                type="button"
                onClick={() => changeResolution(res)}
                className={`h-10 px-3 text-sm font-medium ${resolution === res ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {res} min
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-6 border-b border-gray-200">
        {tabBtn('map', 'Map', MapIcon)}
        {tabBtn('prices', 'Prices table', Table)}
        {tabBtn('flows', 'Flows table', Table)}
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}
      {loading && !series && (
        <div className="flex items-center gap-2 text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading {date}…</div>
      )}

      {series && tab === 'map' && (
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="relative h-[72vh] min-h-[520px] flex-1">
            <ZoneMap series={series} idx={i} bins={bins} theme={theme} onHover={setHover} hover={hover} />
            <Tooltip hover={hover} series={series} idx={i} timeLabel={timeLabel} />
            {loading && <div className="absolute right-3 top-3 rounded-md bg-white px-2 py-1 text-xs text-gray-600 shadow"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Loading…</div>}
          </div>
          <aside className="w-full space-y-5 lg:w-80">
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-800">Net position · {timeLabel}</div>
              {Object.keys(series.net).length ? (
                Object.keys(series.net).map((id) => <NetPositionCard key={id} id={id} value={series.net[id].values[i]} />)
              ) : (
                <div className="text-sm text-gray-500">No exchange data for this day</div>
              )}
            </div>
            <Legend bins={bins} theme={theme} />
            <div className="space-y-1 border-t border-gray-200 pt-3 text-xs text-gray-600">
              <div>{pricedZones} of {ZONES.length} zones priced · {series.flows.length} borders with flows.</div>
              <div>Prices: ENTSO-E day-ahead (SDAC); Nord Pool where ENTSO-E has none. Flows: Nord Pool day-ahead scheduled exchanges on the PL, DE and FR borders, not physical flows. Net position sums the zone's own borders; DE excludes CH (outside SDAC).</div>
              <div>Zone shapes: Natural Earth; NO/SE zone borders approximate. Drag to pan, scroll to zoom.</div>
            </div>
          </aside>
        </div>
      )}

      {series && tab === 'prices' && (
        pricedZones ? (
          <>
            <div className="text-xs text-gray-600">€/MWh. Click a row to select that period.</div>
            <DataTable {...pricesTable(series)} idx={i} onSelect={setIdx} />
          </>
        ) : <div className="text-gray-500">No prices for this day.</div>
      )}

      {series && tab === 'flows' && (
        series.flows.length ? (
          <>
            <div className="text-xs text-gray-600">MW. A positive value flows in the column's arrow direction, a negative one the other way. Net: + import, − export. Click a row to select that period.</div>
            <DataTable {...flowsTable(series)} idx={i} onSelect={setIdx} />
          </>
        ) : <div className="text-gray-500">No exchange data for this day.</div>
      )}
    </div>
  );
};

export default EuropeMap;
